import { FC } from 'react'

import { RenderIf } from 'components/RenderIf'
import { Button } from 'featureSliced/entities/Button'
import { useMyDispatch } from 'featureSliced/shared/hooks/useMyDispatch'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { useTranslations } from 'featureSliced/shared/hooks/useTranslations'
import { useGetTranslatedNames } from 'featureSliced/shared/hooks/useGetTranslatedNames'
import { QRMBucketSlice } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer'
import { IBucketProduct } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer/types'
import { IProduct } from 'featureSliced/shared/store/reducers/CategoriesReducer/types'
import { convertToPrice } from 'featureSliced/shared/utils/convertToPrice'
import { findCatalogProduct } from 'featureSliced/shared/utils/findCatalogProduct'
import { getAmountConfig } from 'featureSliced/shared/utils/weightSteps'
import { roundValueTo } from 'featureSliced/shared/utils/roundValueTo'

import cameraIcon from 'assets/cameraIcon.svg'
import basket from 'assets/basket.svg'
import editIcon from 'assets/basket_small.svg'
import trashCan from 'assets/trashCan.svg'

import {
  WebCartColumn,
  WebCartEmpty,
  WebCartLine,
  WebCartLineBody,
  WebCartThumb,
  WebCartTitle,
  WebCardLabel,
  WebCardValue,
  WebDivider,
  WebIconButton,
  WebStepButton,
  WebStepper,
  WebTotalRow,
  WebTotalValue,
} from './styled'

type WebCartPanelProps = {
  /** total of the local cart, already formatted */
  total: string | null
  onCheckout: () => void
  checkoutLabel: string
  /** opens the product modal — the only place that owns weight-step maths */
  onEditLine: (product: IProduct, line: IBucketProduct) => void
}

export const WebCartPanel: FC<WebCartPanelProps> = ({
  total,
  onCheckout,
  checkoutLabel,
  onEditLine,
}) => {
  const t = useTranslations()
  const dispatch = useMyDispatch()
  const { getProductName } = useGetTranslatedNames()

  const lines = useMySelector((state) => state.bucket.products)
  const categories = useMySelector((state) => state.categories.items)
  const { Currency } = useMySelector((state) => state.app.companyData)

  const { addProductToBucket, removeProductFromBucket } = QRMBucketSlice.actions

  const flatProducts = categories.flatMap((cat) => cat.products)

  const resolve = (line: IBucketProduct) =>
    findCatalogProduct(flatProducts, line.productId)

  const isWeighed = (product?: IProduct | null) =>
    product?.unitSellCode === 2 || product?.unitSellCode === 3

  const step = (line: IBucketProduct, product: IProduct, direction: 1 | -1) => {
    const { step: stepSize, minAmount } = getAmountConfig(
      product.unitSellCode ?? null
    )
    const next = roundValueTo(line.count + direction * stepSize)
    if (next < minAmount) {
      dispatch(removeProductFromBucket(line.uniqueId))
      return
    }
    dispatch(addProductToBucket({ ...line, count: next }))
  }

  return (
    <WebCartColumn data-testid="web-cart-panel">
      <WebCartTitle>
        <WebCardValue as="p">{t('order.yourOrder')}</WebCardValue>
        <RenderIf condition={!!lines.length}>
          <WebCardLabel>{lines.length}</WebCardLabel>
        </RenderIf>
      </WebCartTitle>

      <RenderIf condition={!lines.length}>
        <WebCartEmpty>
          <img src={basket} alt="" width="34" height="26" />
          <WebCardValue>{t('errors.basketIsEmpty')}</WebCardValue>
        </WebCartEmpty>
      </RenderIf>

      {lines.map((line) => {
        const product = resolve(line)
        const weighed = isWeighed(product)
        return (
          <WebCartLine key={line.uniqueId}>
            <WebCartThumb src={product?.imageUrl || cameraIcon} alt="" />
            <WebCartLineBody>
              <WebCardValue>
                {product ? getProductName(product) : ''}
              </WebCardValue>
              <WebStepper>
                {/* weight items keep their maths in the product modal (QRM-524) */}
                <RenderIf condition={!weighed && !!product}>
                  <WebStepButton
                    type="button"
                    aria-label={t('buttons.remove')}
                    onClick={() => step(line, product as IProduct, -1)}
                  >
                    &minus;
                  </WebStepButton>
                </RenderIf>
                <WebCardValue as="span">
                  {convertToPrice(line.count)}
                </WebCardValue>
                <RenderIf condition={!weighed && !!product}>
                  <WebStepButton
                    type="button"
                    aria-label={t('buttons.add')}
                    onClick={() => step(line, product as IProduct, 1)}
                  >
                    +
                  </WebStepButton>
                </RenderIf>
                <RenderIf condition={!!product}>
                  <WebIconButton
                    type="button"
                    aria-label={t('buttons.edit')}
                    title={t('buttons.edit')}
                    onClick={() => onEditLine(product as IProduct, line)}
                  >
                    <img src={editIcon} alt="" />
                  </WebIconButton>
                </RenderIf>
                <WebIconButton
                  type="button"
                  aria-label={t('buttons.remove')}
                  title={t('buttons.remove')}
                  onClick={() =>
                    dispatch(removeProductFromBucket(line.uniqueId))
                  }
                >
                  <img src={trashCan} alt="" />
                </WebIconButton>
              </WebStepper>
            </WebCartLineBody>
            <WebCardValue as="span">
              {convertToPrice(line.price * line.count)}
            </WebCardValue>
          </WebCartLine>
        )
      })}

      <RenderIf condition={!!lines.length}>
        <WebDivider />
        <WebTotalRow>
          <span>{t('common.total')}</span>
          <WebTotalValue>{`${total || convertToPrice(0)} ${
            Currency || ''
          }`}</WebTotalValue>
        </WebTotalRow>
        <Button buttonType="common" width="100%" onClick={onCheckout}>
          {checkoutLabel}
        </Button>
      </RenderIf>
    </WebCartColumn>
  )
}
